import { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import ErrorAlerts from "../../components/Alerts/ErrorAlerts";

export default function LoginPage({ onSubmit }) {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState({ title: "", list: [] });

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate email only when the form is submitted
    if (submitted && name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidEmail = emailRegex.test(value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: isValidEmail ? "" : "Please enter a valid email address",
      }));
    }

    // Validate password only when the form is submitted
    if (submitted && name === "password") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password:
          value.length < 6 ? "Password must be at least 6 characters long" : "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Set submitted to true to trigger validation
    setSubmitted(true);
    setLoading(true);
    // Form submission logic
    if (
      formData.email &&
      formData.password &&
      !errors.email &&
      !errors.password
    ) {
      // Form is valid, proceed with submission
      if (onSubmit instanceof Function) {
        const resp = await onSubmit(formData.email, formData.password);

        if (resp && resp.success) {
        } else if (resp && resp.data?.message) {
          setErrorMessage({ title: resp.data.message });
        } else {
          setErrorMessage({
            title: "An error occured while attempting to sign in",
          });
        }
      }
      setLoading(false);
    } else {
      // Form is invalid, do not proceed with submission

      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img
            className="mx-auto h-10 w-auto"
            src="/logo.png"
            alt="Your Company"
          />
          <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Sign in to your account
          </h2>
          {(errorMessage.title ||
            (Array.isArray(errorMessage.list) &&
              errorMessage.list.length > 0)) && (
            <ErrorAlerts title={errorMessage.title} list={errorMessage.list} />
          )}
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form
            onSubmit={handleSubmit}
            className="space-y-6"
            action="#"
            method="POST"
          >
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Email address
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 disabled:bg-blue-200 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none ${
                    submitted && errors.email ? "border-red-500" : ""
                  }`}
                />
                {submitted && errors.email && (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Password
                </label>
                {/* <div className="text-sm">
                  <a href="#" className="font-semibold text-blue-600 hover:text-blue-500">
                    Forgot password?
                  </a>
                </div> */}
              </div>
              <div className="mt-2 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  className={`block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 disabled:bg-blue-200 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none ${
                    submitted && errors.password ? "border-red-500" : ""
                  }`}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-700 focus:outline-none"
                  onClick={togglePasswordVisibility}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {submitted && errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>

            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:bg-blue-200 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
                disabled={
                  (submitted && (errors.email || errors.password)) || loading
                }
              >
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
